import React, { useState } from 'react';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';

import * as Api from '../../../api/api';
import { UiButton } from '../../../ui/ui-button';
import { UiDialog } from '../../../ui/ui-dialog';

import Message from '../../../widgets/legacy-message';
import { ReactComponent as LoadingLogo } from '../../../widgets/loading-logo.svg';
import { EmployeeDirectoryImportFileParser } from '../util/import-file-parser';

type Props = EmployeeDirectoryWizardStepProps;

const DEFAULT_DISPLAYED_ROWS = 5;

export const EmployeeDirectoryImportDataConfirmation = ({
  employeeDirectoryImportId,
  onNext,
  onCancel,
  onPrevious,
  previousStepData,
  PageIndicator,
}: Props) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [limitRows, setLimitRows] = useState<boolean>(true);

  if (!previousStepData) {
    return null;
  }

  const { parsedWorksheet, columnMappings, headers, headerRow, noHeaderRow, selectedWorksheet } =
    previousStepData;

  const handleOnNext = async () => {
    let response = null;
    setIsSaving(true);
    try {
      response = await Api.post(
        `/employee-directory-import/${employeeDirectoryImportId}/parse-file`,
        {
          body: JSON.stringify({ columnMappings, headerRow, noHeaderRow, selectedWorksheet }),
        },
      );
    } catch (e) {
      setErrorMessage('There was an error saving data');
    }
    const result = await response?.json();
    setIsSaving(false);
    if (!result || result.errorMessage) {
      setErrorMessage('There was an error saving data');
    } else {
      onNext({ columnMappings });
    }
  };

  const handleOnBack = () => {
    onPrevious(previousStepData);
  };

  const handleClickViewMoreRows = () => setLimitRows(false);

  const unIgnoredHeaders = headers?.filter(
    (h: any) => !EmployeeDirectoryImportFileParser.isIgnored(h, columnMappings),
  );
  const rowsWithErrors = parsedWorksheet
    ? EmployeeDirectoryImportFileParser.getRowsWithErrors(parsedWorksheet, columnMappings)
    : null;
  const rowsWithoutErrors = parsedWorksheet
    ? EmployeeDirectoryImportFileParser.getRowsWithoutErrors(parsedWorksheet, columnMappings)
    : null;
  let limitedRowsWithoutErrors = rowsWithoutErrors;
  if (limitRows && rowsWithoutErrors) {
    limitedRowsWithoutErrors = rowsWithoutErrors.slice(0, DEFAULT_DISPLAYED_ROWS);
  }
  const maxColumns = rowsWithoutErrors
    ? rowsWithoutErrors.reduce((acc, row) => {
        const curr = Object.keys(row);
        if (curr.length > acc) {
          return curr.length;
        }
        return acc;
      }, 1) + 1
    : 1;

  return (
    <>
      <UiDialog.Content>
        <PageIndicator />
        <span className="typo-h5">Confirmation of Resulting Data</span>
        <p className="typo-subtitle">
          The table below shows all of the data that will be imported into Team Engine
        </p>
        {errorMessage ? <Message variant="error">{errorMessage}</Message> : null}
        {(!parsedWorksheet || !headers) && !errorMessage ? (
          <div className="u-textAlign-center">
            <LoadingLogo />
          </div>
        ) : null}
        {rowsWithErrors && rowsWithErrors.length ? (
          <>
            <span className="typo-h5">Rows with errors or missing data</span>
            <p className="typo-subtitle">
              Theses rows have data that we failed to parse. Either upload a new file with the data
              fixed, or let the customer we failed to import these values
            </p>
            {rowsWithErrors && unIgnoredHeaders ? (
              <div className="u-width-100 u-overflow-auto">
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      {unIgnoredHeaders.map((h: any) =>
                        EmployeeDirectoryImportFileParser.getParsedHeaderCell(h, columnMappings),
                      )}
                    </TableRow>
                    {rowsWithErrors.map((row: any) => (
                      <TableRow>
                        {unIgnoredHeaders.map((h: any) =>
                          EmployeeDirectoryImportFileParser.getParsedValueCell(
                            row,
                            h,
                            columnMappings,
                          ),
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : null}
            <div className="typo-h5 u-margin-y">Parsed Data Preview</div>
          </>
        ) : null}
        {limitedRowsWithoutErrors && unIgnoredHeaders ? (
          <div className="u-width-100 u-overflow-auto">
            <Table size="small">
              <TableBody>
                <TableRow>
                  {unIgnoredHeaders.map((h: any) =>
                    EmployeeDirectoryImportFileParser.getParsedHeaderCell(h, columnMappings),
                  )}
                </TableRow>
                {limitedRowsWithoutErrors.map((row: any) => (
                  <TableRow>
                    {unIgnoredHeaders.map((h: any) =>
                      EmployeeDirectoryImportFileParser.getParsedValueCell(row, h, columnMappings),
                    )}
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={maxColumns}>
                    {limitRows ? (
                      <div
                        className="typo-link"
                        role="presentation"
                        onClick={handleClickViewMoreRows}
                      >
                        View all {rowsWithoutErrors?.length ?? ''} rows...
                      </div>
                    ) : null}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        ) : null}
      </UiDialog.Content>
      <UiDialog.Actions>
        <UiButton variant="dismiss" onClick={onCancel}>
          Cancel
        </UiButton>
        <div>
          <UiButton variant="secondary" onClick={handleOnBack}>
            Back
          </UiButton>
          <UiButton
            variant="primary"
            className="u-margin-left"
            disabled={Boolean(errorMessage)}
            onClick={handleOnNext}
            loading={isSaving}
          >
            Next
          </UiButton>
        </div>
      </UiDialog.Actions>
    </>
  );
};
